import React from 'react';
import { AlertBanner, Spinner, CardDefault, TruncatedText, sparkToast, SparkModalPortal, Modal } from '@spark';
import { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { debounce, isEqual } from 'lodash';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { getPredictionColor } from '@/shared/utils/helpers';
import { SubTaskType } from '@/shared/domain/taskTypes';
import ModelResponsesTable from './ModelResponsesTable';
import QualityMetrics from './QualityMetrics';
import QualityMetricsVersionComparison from './QualityMetricsVersionComparison';
import VersionsDiffModal from './versionsDiff/VersionsDiffModal';
import MetricsChart from './MetricsChart';
import FilterBar from './FilterBar';
import ReplaceBenchmarkModal from './ReplaceBenchmarkModal';
import ImproveFlowModal from './ImproveFlow/ImproveFlowModal';
import TabNavigation from './TabNavigation';
import EvaluationEmptyState from './EvaluationEmptyState';
import EvaluationTrainingInProgressState from './EvaluationTrainingInProgressState';
import { isTrainingInProgress } from '@/features/evaluation/utils/evaluationEmptyState';
import EvaluationProcessingState from './EvaluationProcessingState';
import UnsupportedLanguagesWarning from './UnsupportedLanguagesWarning';
import ErrorAnalysis from './ErrorAnalysis';
import LoadingTaskData from '@/features/tasks/components/LoadingTaskData';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { ALL_LABELS } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import SummarizationEvaluation from './Summarization/SummarizationEvaluation';
import IncompleteTrainingAlert from './IncompleteTrainingAlert';
import EvaluationErrorsAlert, { hasEvaluationErrorRecords } from './EvaluationErrorsAlert';
import { teacherStatusForDisplayedRun } from '../utils/mergeTeacherEvaluationStatus';
import { useEvaluationResync } from '../hooks/useEvaluationResync';
import { recalculationRunFromResponse } from '../utils/finishEvaluationProgress';
import { isEvaluationErrorAnswer } from '../utils/evaluationErrorAnswer';
import { createEvaluationQueryOrder } from '../utils/evaluationQueryOrder';
import { buildCompareOptions } from './compareOptions';
import { useStableTeacherNameForCompare } from './useStableTeacherNameForCompare';
import { formatTeacherModelShortLabel } from '@/features/model-setup/utils/teacherModelUtils';
import { getLabelCountTotal } from "./labelCountUtils";
import {
  isNerEntitiesThresholdContext,
  mergeNerThresholdCandidates,
  nerBaselineThresholds,
  resolveOverviewSampleCounters,
  resolvePlayWithThresholdLabel,
} from '@/features/evaluation/utils/nerThresholdUi';
import LabelConfusionMatrix from './LabelConfusionMatrix';
import EvaluationViewTabs, { EVAL_VIEW_TABS } from './EvaluationViewTabs';
import PerformanceTab from './Performance/PerformanceTab';
import {
  DEFAULT_COST_CONFIG,
  DEFAULT_PERF_FILTERS,
} from '@/features/evaluation/api/performanceMetricsApi';
import { EE_RENDER_MODES } from './EntityExtractionsTable';

const ErrorDetailsAlert = ({ title, message, details, type = 'error', showIcon = true, style }) => {
    const [open, setOpen] = React.useState(false);
    const detailsText = React.useMemo(() => {
        if (details == null) return '';
        if (typeof details === 'string') return details;
        try { return JSON.stringify(details, null, 2); } catch { return String(details); }
    }, [details]);
    const hasDetails = Boolean(detailsText && detailsText.trim().length > 0);
    return (
        <>
            <div style={style}>
                <AlertBanner
                    feedback={type}
                    showIcon={showIcon}
                    text={message}
                    showButton={hasDetails}
                    buttonType="text"
                    buttonLabel="View error details"
                    onActionClick={() => setOpen(true)}
                    showCloseButton={false}
                />
            </div>
            {hasDetails && (
                <SparkModalPortal open={open} onClose={() => setOpen(false)} size="wide">
                    <Modal
                        title={title || 'Error details'}
                        showSubtitle={false}
                        showClose
                        onCloseClick={() => setOpen(false)}
                        showFooter={false}
                        showOverlay={false}
                    >
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{detailsText}</pre>
                    </Modal>
                </SparkModalPortal>
            )}
        </>
    );
};
import ThresholdPanel from './ThresholdPanel';
import ModelDetailsDrawer from '@/features/trainings/components/ModelDetailsDrawer';
import {
  modelDrawerDownloadBenchmark,
  modelDrawerUndeploy,
} from '@/features/trainings/components/modelDrawerTrainActions';
import {
  ConfigureDeploymentFlowModals,
  useConfigureDeploymentFlow,
} from '@/features/deployments';
import { submitConfigureDeployment } from '@/features/deployments/components/configureDeployment/submitConfigureDeployment';
import { buildDefaultDeployPayload } from '@/features/deployments/components/configureDeployment/buildDefaultDeployPayload';
import { fetchTrainingsForConfigureModal } from '@/features/deployments/components/configureDeployment/fetchTrainingsForConfigureModal';
import { buildModelDrawerVersionFromEvaluation } from '@/features/trainings/components/trainingsUtils';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { useAuth } from '@/features/auth/AuthContext';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import TaskEvaluationOverview from './TaskEvaluationOverview';
import { TASK_NESTED_MODEL_DRAWER_BODY_CLASS } from '@/features/tasks/components/Task/taskDrawerLayout';


const calculateImprovement = (oldValue, newValue) => {
  // Ensure inputs are numbers
  const oldNum = Number(oldValue);
  const newNum = Number(newValue);

  // Calculate percentage difference
  const difference = newNum - oldNum;
  const percentageChange = difference === 0 ? 0 : (difference / oldNum) * 100;

  // No meaningful comparison (e.g. teacher metrics missing/failed -> baseline
  // of 0 yields Infinity, non-numeric inputs yield NaN): render as "-".
  if (!Number.isFinite(percentageChange)) {
    return '';
  }

  // Format to one decimal place and add + sign for positive values
  const formattedImprovement = percentageChange >= 0
    ? `+${percentageChange.toFixed(1)}%`
    : `${percentageChange.toFixed(1)}%`;

  return formattedImprovement;
};

// "2" -> "2.0", "5.02" -> "5.02" — matches how the Figma designs render run versions.
const formatMinorVersionLabel = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Number.isInteger(num) ? `${num}.0` : `${num}`;
};

const clientId = "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";
export const useEvaluationState = (llmUsecaseId, activeTab, taskType) => {
  const [evaluation, setEvaluation] = useState({
    loading: true,
    result: null,
  });

  const urlParams = new URLSearchParams(window.location.search);
  const urlDefaultVersion = urlParams.get('version');

  const [selectedVersion, setSelectedVersion] = useState(parseFloat(urlDefaultVersion) || null);
  const [versionToCompare, setVersionToCompare] = useState(null);
  // Lock the response to a specific benchmark row (recalc minor version) selected
  // from the Model Details drawer's Benchmarks tab. `null` means "use latest".
  const [selectedBenchmarkEventId, setSelectedBenchmarkEventId] = useState(null);

  const [queryOrder] = useState(createEvaluationQueryOrder);

  // A background query (realtime resync) shows no loading state, keeps the
  // current result when it fails, and keeps the same object when nothing
  // changed, so derived state such as threshold baselines is not reset. An
  // answer that a newer query superseded is ignored.
  const queryEvaluation = useCallback((version, versionToCompare, activeTab, benchmarkEventId, { background = false } = {}) => {
    const compare_with = versionToCompare === 'file' ? {
      compare_with_file: true
    } : {
      compare_with_version: versionToCompare
    }
    const evaluation_data = {
      client_id: clientId,
      llm_usecase_id: llmUsecaseId,
      version,
      comparison_mode: versionToCompare !== null,
      ...compare_with,
      sub_task_type: taskType !== TaskType.NER_WITH_RELATIONS
        ? null
        : activeTab == "entities" ? SubTaskType.Ner : SubTaskType.Relations,
      selected_distillation_event_id: benchmarkEventId || null,
    }

    const isCurrent = queryOrder.start(background);
    if (!background) {
      setEvaluation((prev) => ({
        ...prev,
        loading: true,
      }));
    }
    data_from_server_post(
      "evaluation",
      evaluation_data,
      (result) => {
        if (!isCurrent()) return;
        if (background) {
          // A background refresh keeps the current results when the backend itself failed.
          if (isEvaluationErrorAnswer(result)) return;
          setEvaluation((prev) => (isEqual(prev.result, result) ? prev : { loading: false, result }));
          return;
        }
        setEvaluation({
          loading: false,
          result: result,
        });
      },
      (error) => {
        if (background || !isCurrent()) return;
        setEvaluation({
          loading: false,
          result: null,
          error: error,
        });
      }
    );
  }, [llmUsecaseId, selectedVersion, versionToCompare, activeTab, taskType])

  const updateVersion = useCallback((version, benchmarkEventId = null) => {
    setSelectedVersion(version);
    // Switching the major version invalidates any locked benchmark from a different
    // major; callers that remembered a benchmark for the target major (the
    // "Replace Benchmark" session map) pass it explicitly.
    setSelectedBenchmarkEventId(benchmarkEventId);
    queryEvaluation(version, versionToCompare, activeTab, benchmarkEventId);
  }, [versionToCompare, queryEvaluation, activeTab, taskType])

  const updateVersionToCompare = useCallback((version) => {
    setVersionToCompare(version);
    queryEvaluation(selectedVersion, version, activeTab, selectedBenchmarkEventId);
  }, [selectedVersion, queryEvaluation, activeTab, taskType, selectedBenchmarkEventId])

  const updateSelectedBenchmarkEventId = useCallback((benchmarkEventId) => {
    setSelectedBenchmarkEventId(benchmarkEventId || null);
    queryEvaluation(selectedVersion, versionToCompare, activeTab, benchmarkEventId || null);
  }, [selectedVersion, versionToCompare, queryEvaluation, activeTab])

  const refresh = useCallback((options) => {
    queryEvaluation(selectedVersion, versionToCompare, activeTab, selectedBenchmarkEventId, options);
  }, [selectedVersion, versionToCompare, queryEvaluation, activeTab, taskType, selectedBenchmarkEventId])

  return {
    loading: evaluation.loading,
    result: evaluation.result,
    error: evaluation.error,
    refresh,
    updateVersion: updateVersion,
    selectedVersion,
    updateVersionToCompare,
    versionToCompare,
    selectedBenchmarkEventId,
    updateSelectedBenchmarkEventId,
  }
}

/** Fresh copy so we never mutate the module-level `DEFAULT_PERF_FILTERS` object. */
function newPerformanceFiltersState() {
  return {
    ...DEFAULT_PERF_FILTERS,
    environments: DEFAULT_PERF_FILTERS.environments.map((e) => ({ ...e })),
  };
}

const EvaluationNonSummarization = ({
  moveToScreen,
  localUsecase,
  currentUsecaseId,
  evaluationProgress,
  setEvaluationProgress,
  evaluationResultsReady,
  setProgressStatus,
  trainStatuses,
  embeddedInTaskDrawer = false,
  onRetrainClick = null,
}) => {
  const [activeTab, setActiveTab] = useState('entities'); // For NER_WITH_RELATIONS task type
  const [viewTab, setViewTab] = useState(EVAL_VIEW_TABS.OVERVIEW); // Overview / Samples / Performance for non-NER_WITH_RELATIONS
  const { role } = useAuth();
  // Configure Deployment's Add-Task picker needs the live usecase docs.
  // `updateStatus` applies optimistic deploy/undeploy lifecycle statuses.
  // `handleWsMessage` doubles as the optimistic local merge for the teacher
  // evaluation cancel flow (same merge path the websocket updates use).
  const { allUsecases, updateStatus, handleWsMessage, realtimeResyncTick } = useContext(GlobalContext);

  const {
    loading,
    result: evaluationResult,
    error, // todo - show error if query fails
    refresh,
    updateVersion,
    updateVersionToCompare,
    selectedVersion: localVersion,
    versionToCompare,
    selectedBenchmarkEventId,
    updateSelectedBenchmarkEventId,
  } = useEvaluationState(currentUsecaseId, activeTab, localUsecase?.task_type);

  // Session-only "Replace Benchmark" choices: major version (string key) ->
  // { event_id, version } of the run whose benchmark the user picked for that
  // major. Never persisted; drives the "(v5.02)" labels in the "To" dropdown
  // and which run is locked when the user switches to that major.
  const [benchmarkSelectionByMajor, setBenchmarkSelectionByMajor] = useState({});
  // Major version for which the "Replace Benchmark" modal is open (null = closed).
  const [replaceBenchmarkMajor, setReplaceBenchmarkMajor] = useState(null);

  // Selecting a major in the "To" dropdown re-applies that major's remembered
  // benchmark choice (if any) instead of always falling back to the latest run.
  const setSelectedVersion = useCallback(
    (version) => {
      const stored =
        version != null
          ? benchmarkSelectionByMajor[String(Math.floor(Number(version)))]
          : null;
      updateVersion(version, stored?.event_id ?? null);
    },
    [benchmarkSelectionByMajor, updateVersion],
  );

  const [optimisticRecalc, setOptimisticRecalc] = useState(null); // { major: number, baseline_minor: number, run?: { runId, version } }
  const [modelDetailsOpen, setModelDetailsOpen] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);

  useEffect(() => {
    if (!embeddedInTaskDrawer) return undefined;
    if (modelDetailsOpen) {
      document.body.classList.add(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
    } else {
      document.body.classList.remove(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
    }
    return () => document.body.classList.remove(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
  }, [modelDetailsOpen, embeddedInTaskDrawer]);

  // Performance-tab filters live here so they survive clearing Compare: (that
  // triggers `queryEvaluation` -> `loading: true` -> Performance unmounts).
  // If this state lived only inside `PerformanceTab`, remount would reset
  // concurrency back to the default serial.
  const [performanceFilters, setPerformanceFilters] = useState(newPerformanceFiltersState);
  const [performanceCostConfig, setPerformanceCostConfig] = useState(() => ({
    ...DEFAULT_COST_CONFIG,
  }));

  useEffect(() => {
    setPerformanceFilters(newPerformanceFiltersState());
    setPerformanceCostConfig({ ...DEFAULT_COST_CONFIG });
  }, [currentUsecaseId]);

  // Version options are driven by the backend evaluation response.
  const versionOptions = evaluationResult?.versions || [];
  const selectedVersionForRefresh = useMemo(() => {
    if (localVersion != null) return localVersion;
    if (versionOptions.length > 0) return Math.max(...versionOptions);
    return null;
  }, [localVersion, versionOptions]);

  const lastAutoRefreshKeyRef = useRef(null);
  useEffect(() => {
    // Fetch exactly once when websocket signals results are ready.
    if (!currentUsecaseId) return;

    const allSignals = evaluationResultsReady || {};
    const preferredMajor =
      selectedVersionForRefresh == null ? null : Math.floor(Number(selectedVersionForRefresh));

    const preferredKey =
      Number.isFinite(preferredMajor) ? `${currentUsecaseId}_${preferredMajor}` : null;

    const signalKey = (() => {
      if (preferredKey && allSignals[preferredKey]) return preferredKey;
      // Fallback: use the newest signal for this usecase (covers cases where the evaluation
      // response is empty while processing, so we don't have a version to key off).
      const keys = Object.keys(allSignals).filter((k) => k.startsWith(`${currentUsecaseId}_`));
      if (keys.length === 0) return null;
      return keys.reduce((best, cur) => {
        const b = allSignals[best];
        const c = allSignals[cur];
        return (c?.ts || 0) > (b?.ts || 0) ? cur : best;
      }, keys[0]);
    })();

    if (!signalKey) return;
    const signal = allSignals[signalKey];
    const major = Number(signalKey.split("_")[1]);

    const refreshKey = `${signalKey}:${signal?.ts}`;
    if (lastAutoRefreshKeyRef.current === refreshKey) return;
    lastAutoRefreshKeyRef.current = refreshKey;
    refresh();
    // If we were showing an optimistic processing overlay, unblock the UI now.
    setOptimisticRecalc(null);
  }, [evaluationResultsReady, currentUsecaseId, selectedVersionForRefresh, refresh]);


  useEffect(() => { // when usecase changes, load the evaluation
    if (!currentUsecaseId) {
      return;
    }

    refresh();
  }, [currentUsecaseId]);

  // Refresh once when this task's training transitions to canceled/failed so the persisted
  // stopped/failed marker (last_training_outcome) shows in the empty state without a reload.
  const lastOwnTrainStatusRef = useRef(null);
  useEffect(() => {
    if (!currentUsecaseId) {
      return;
    }
    const entry = Object.values(trainStatuses || {}).find(
      (status) => status?.usecase_id === currentUsecaseId || status?.llm_usecase_id === currentUsecaseId,
    );
    const nextStatus = entry?.status ?? null;
    const prevStatus = lastOwnTrainStatusRef.current;
    lastOwnTrainStatusRef.current = nextStatus;
    if (nextStatus !== prevStatus && (nextStatus === 'canceled' || nextStatus === 'failed')) {
      refresh();
    }
  }, [trainStatuses, currentUsecaseId, refresh]);

  useEvaluationResync({
    clientId,
    usecaseId: currentUsecaseId,
    realtimeResyncTick,
    reloadInBackground: () => refresh({ background: true }),
    evaluation: evaluationResult,
    evaluationProgress,
    setEvaluationProgress,
    pendingRun: optimisticRecalc?.run ?? null,
  });

  useEffect(() => { // when active tab changes (NER_WITH_RELATIONS), reload evaluation
    if (!currentUsecaseId) {
      return;
    }

    // Reset class filter when switching tabs
    setSelectedLabel(ALL_LABELS);

    refresh();
  }, [activeTab]);

  const [choseCurrent, setChoseCurrent] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(ALL_LABELS);
  const [displayNames, setDisplayNames] = useState({});
  const [liveThresholds, setLiveThresholds] = useState(null);
  const [debouncedThresholdsForTable, setDebouncedThresholdsForTable] = useState(null);
  const [customThresholdMetrics, setCustomThresholdMetrics] = useState(null);
  const [customThresholdLoading, setCustomThresholdLoading] = useState(false);
  const [selectedMatchType, setSelectedMatchType] = useState('partial')
  // Non-null while the "Adjust Threshold" mode is active; holds the filter
  // values captured just before the first click so the Back button can
  // restore them.
  const [thresholdModeSnapshot, setThresholdModeSnapshot] = useState(null);

  const baselineThresholds = useMemo(() => {
    const config = evaluationResult?.selected_threshold_config;
    const saved = evaluationResult?.selected_thresholds;
    if (config === 'custom' && saved) return saved;
    const calibrated = evaluationResult?.threshold_candidates?.calibrated;
    if (!calibrated) return null;
    const defaults = {};
    for (const [label, data] of Object.entries(calibrated)) {
      const candidates = data.candidates || [];
      if (candidates.length > 0) {
        let closest = candidates[0];
        let minDist = Math.abs(candidates[0].threshold - data.optimal_threshold);
        for (const c of candidates) {
          const dist = Math.abs(c.threshold - data.optimal_threshold);
          if (dist < minDist) { minDist = dist; closest = c; }
        }
        defaults[label] = closest.threshold;
      } else {
        defaults[label] = data.optimal_threshold;
      }
    }
    return defaults;
  }, [evaluationResult?.selected_threshold_config, evaluationResult?.selected_thresholds, evaluationResult?.threshold_candidates]);

  const teacherModelProcessing = evaluationResult?.teacher_model_processing || false;
  const teacherNameForCompare = useStableTeacherNameForCompare({
    evaluatedTeacherModelName: evaluationResult?.evaluated_teacher_model_name,
    teacherModelProcessing: evaluationResult?.teacher_model_processing,
    usecaseTeacherModel: localUsecase?.teacher_model,
  });

  const currentToCompare = useMemo(() => {
    if (versionToCompare != null) {
      return versionToCompare;
    }
    if (choseCurrent === null) {
      if (!localUsecase?.comparison_mode) {
        return null;
      }
      if (teacherNameForCompare) {
        return 'oldModel';
      }
      return null;
    }
    return choseCurrent ? 'oldModel' : null;
  }, [choseCurrent, versionToCompare, localUsecase?.comparison_mode, teacherNameForCompare]);

  useEffect(() => {
    if (!teacherNameForCompare && currentToCompare === 'oldModel') {
      setChoseCurrent(false);
      updateVersionToCompare(null);
    }
  }, [teacherNameForCompare, currentToCompare, updateVersionToCompare]);

  const setCurrentToCompare = (version) => {
    // AntD allowClear emits `undefined`; FilterBar normalizes that to `null`.
    // When clearing, we must also reset `versionToCompare`, otherwise numeric
    // SpecificAI versions keep "winning" the derived `currentToCompare` value.
    if (version == null) {
      setChoseCurrent(false);
      updateVersionToCompare(null);
      return;
    }

    setChoseCurrent(true);
    if (version === 'oldModel') {
      updateVersionToCompare(null);
    } else if (version === 'file') {
      updateVersionToCompare('file');
    } else {
      updateVersionToCompare(version);
    }
  }


  const evaluationExists = evaluationResult !== null && evaluationResult["success"];
  const showImproveEvals = evaluationResult?.dataset_origins?.some(origin => origin !== "human_created") || false;

  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const languageOptions = useMemo(() => {
    const byLang = evaluationResult?.all_metrics?.by_language;
    if (!byLang || typeof byLang !== "object") return [];
    return Object.keys(byLang).sort();
  }, [evaluationResult?.all_metrics]);

  const activeAllMetrics = useMemo(() => {
    const all = evaluationResult?.all_metrics;
    if (!selectedLanguage) return all;
    const byLang = all?.by_language;
    if (!byLang || typeof byLang !== "object") return all;
    return byLang?.[selectedLanguage] || all;
  }, [evaluationResult?.all_metrics, selectedLanguage]);

  // Overview pies must track Adjust Threshold like Quality Metrics: overlay
  // flat live eval_sample_counters onto the nested stored newModel buckets.
  const liveOverviewSampleCounters = useMemo(
    () =>
      resolveOverviewSampleCounters(
        activeAllMetrics?.eval_sample_counters,
        customThresholdMetrics,
      ),
    [activeAllMetrics?.eval_sample_counters, customThresholdMetrics],
  );

  const labels = useMemo(() => {
    const evalClasses = evaluationResult?.classes || [];
    const allMetrics = activeAllMetrics;

    const union = new Set(evalClasses);

    // Also include labels that appear in the evaluation metrics payload (benchmarks),
    // even if they are not present in the dataset/usecase counts.
    const addMetricKeys = (metricsByLabel) => {
      if (!metricsByLabel || typeof metricsByLabel !== "object") return;
      Object.keys(metricsByLabel).forEach((k) => union.add(k));
    };

    // For NER_WITH_RELATIONS, make the filter options tab-aware.
    // When benchmark counts are available for this distillation version, they are the
    // source of truth (prevents showing stale/mismatched label variants).
    if (localUsecase?.task_type === TaskType.NER_WITH_RELATIONS) {
      if (activeTab === "relations") {
        const benchmarkRelationKeys = Object.keys(
          evaluationResult?.benchmark_relation_names_count || {}
        );
        if (benchmarkRelationKeys.length > 0) {
          return benchmarkRelationKeys;
        }

        Object.keys(localUsecase?.relation_names_count || {}).forEach((k) => union.add(k));
        addMetricKeys(allMetrics?.eval_relation_only_category_metrics);
        addMetricKeys(allMetrics?.eval_class_metrics);
        return Array.from(union);
      }
      if (activeTab === "entities") {
        const benchmarkEntityKeys = Object.keys(
          evaluationResult?.benchmark_entity_names_count || {}
        );
        if (benchmarkEntityKeys.length > 0) {
          return benchmarkEntityKeys;
        }

        Object.keys(localUsecase?.entity_names_count || {}).forEach((k) => union.add(k));
        addMetricKeys(allMetrics?.eval_class_metrics);
        addMetricKeys(allMetrics?.eval_span_level_metrics);
        return Array.from(union);
      }
    }

    // NER: prefer benchmark entity counts as the source of truth when available.
    if (localUsecase?.task_type === TaskType.NER) {
      const benchmarkEntityKeys = Object.keys(
        evaluationResult?.benchmark_entity_names_count || {}
      );
      if (benchmarkEntityKeys.length > 0) {
        return benchmarkEntityKeys;
      }

      Object.keys(localUsecase?.entity_names_count || {}).forEach((k) => union.add(k));
      addMetricKeys(allMetrics?.eval_class_metrics);
      addMetricKeys(allMetrics?.eval_span_level_metrics);
      return Array.from(union);
    }

    // Classification: prefer benchmark label counts as the source of truth when available.
    // A non-predefined (synthetic) confidence-threshold "Other" fallback must not
    // appear as a selectable class; a predefined one is a real label and stays.
    const rejectionLabelName = evaluationResult?.rejection_label_name;
    const rejectionIsPredefined = evaluationResult?.rejection_label_is_predefined;
    const filterRejection = (arr) =>
      rejectionLabelName && !rejectionIsPredefined
        ? arr.filter((label) => label !== rejectionLabelName)
        : arr;

    const benchmarkLabelKeys = Object.keys(
      evaluationResult?.benchmark_label_names_count || {}
    );
    if (benchmarkLabelKeys.length > 0) {
      return filterRejection(benchmarkLabelKeys);
    }

    return filterRejection(evalClasses);
  }, [evaluationResult?.classes, activeAllMetrics, localUsecase?.task_type, localUsecase, activeTab,
  evaluationResult?.benchmark_entity_names_count, evaluationResult?.benchmark_label_names_count,
  evaluationResult?.benchmark_relation_names_count,
  evaluationResult?.rejection_label_name, evaluationResult?.rejection_label_is_predefined]);

  const isNerEntitiesContext = isNerEntitiesThresholdContext(
    localUsecase?.task_type,
    activeTab,
  );

  // Slider emit and /compute-metrics-for-thresholds must share this map.
  // Classification ``baselineThresholds`` is null when calibrated candidates
  // were never persisted; NER still has a 0.0-per-type default.
  const liveMetricsBaseline = useMemo(() => {
    if (isNerEntitiesContext) {
      return nerBaselineThresholds(labels, evaluationResult) || baselineThresholds;
    }
    return baselineThresholds;
  }, [isNerEntitiesContext, labels, evaluationResult, baselineThresholds]);

  // Build a label -> count map based on the per-version distillation-event counts snapshot
  // returned by the evaluation endpoint (source of truth).
  //
  const labelTotals = useMemo(() => {
    const taskType = localUsecase?.task_type;
    if (!taskType) return null;

    const isEntitiesContext =
      taskType === TaskType.NER ||
      (taskType === TaskType.NER_WITH_RELATIONS && activeTab === "entities");
    const isRelationsContext =
      taskType === TaskType.NER_WITH_RELATIONS && activeTab === "relations";

    const countsByLabel = isEntitiesContext
      ? evaluationResult?.benchmark_entity_names_count
      : isRelationsContext
        ? evaluationResult?.benchmark_relation_names_count
        : evaluationResult?.benchmark_label_names_count;

    if (!countsByLabel || typeof countsByLabel !== "object") return null;
    const keys = Object.keys(countsByLabel);
    if (keys.length === 0) return null;

    return (labels || []).reduce((acc, label) => {
      const total = getLabelCountTotal(countsByLabel?.[label]);
      if (typeof total === "number" && Number.isFinite(total)) {
        acc[label] = total;
      }
      return acc;
    }, {});
  }, [
    localUsecase?.task_type,
    activeTab,
    labels,
    evaluationResult?.benchmark_entity_names_count,
    evaluationResult?.benchmark_relation_names_count,
    evaluationResult?.benchmark_label_names_count,
  ]);

  const hasIncompleteTrainings = evaluationResult?.has_incomplete_trainings || false;
  const studentModelProcessing = evaluationResult?.student_model_processing || false;
  const teacherModelFailed = evaluationResult?.teacher_model_failed || false;
  const teacherModelError = evaluationResult?.teacher_model_error || null;
  const teacherModelErrorDetails = evaluationResult?.teacher_model_error_details || null;
  const teacherModelCancelled = evaluationResult?.teacher_model_cancelled || false;
  const teacherModelWarning = evaluationResult?.teacher_model_warning || null;
  const teacherModelEtaSeconds = evaluationResult?.teacher_model_eta_seconds || null;
  const teacherModelQuotaLink = evaluationResult?.teacher_model_quota_link || null;
  const teacherModelAzureMode = evaluationResult?.teacher_model_azure_mode || null;
  const teacherModelFailureDetails = evaluationResult?.teacher_model_failure_details || null;

  // Cancel an in-flight teacher evaluation. The run to cancel is the one that
  // owns the live usecase-level status (the most recently started run).
  const handleCancelTeacherEvaluation = useCallback(async () => {
    const distillationEventId =
      localUsecase?.last_teacher_evaluation_status?.distillation_event_id ||
      evaluationResult?.selected_distillation_event_id ||
      evaluationResult?.distillation_event_id ||
      null;
    if (!distillationEventId || !localUsecase?._id) {
      sparkToast.error('Could not identify the running teacher evaluation');
      return false;
    }
    const response = await data_from_server_post('cancel-teacher-evaluation', {
      client_id: localUsecase?.client_id,
      llm_usecase_id: localUsecase?._id,
      distillation_event_id: distillationEventId,
    });
    if (!response?.success) {
      sparkToast.error(response?.message || 'Could not cancel the teacher evaluation');
      return false;
    }
    // Optimistically surface the "cancelling" state in the banner right away
    // through the same merge path the websocket updates use, so the user
    // never needs a refresh (or a healthy socket) to see it. Counters are
    // omitted, so the merge preserves the existing progress numbers.
    handleWsMessage({
      action: 'updateTeacherEvaluationStatus',
      usecase_id: localUsecase._id,
      is_running: true,
      distillation_event_id: distillationEventId,
      warning: 'Cancellation requested - stopping the teacher evaluation...',
    });
    sparkToast.info('Cancellation requested - the teacher evaluation is stopping');
    // Refetch the evaluation snapshot: the backend treats a persisted cancel
    // request as terminal, so this flips the teacher column to "Cancelled"
    // immediately — no manual page refresh, no dependency on the handler
    // acknowledging the cancel.
    refresh();
    return true;
  }, [localUsecase, evaluationResult, handleWsMessage, refresh]);
  const evaluationErrors = evaluationResult?.evaluation_errors || null;
  const teacherEvaluationErrors = evaluationResult?.teacher_evaluation_errors || null;
  const showEvaluationErrorsAlert = hasEvaluationErrorRecords(
    evaluationErrors,
    teacherEvaluationErrors
  );
  const all_cached_from_sdk_commercial_model = evaluationResult?.all_cached_from_sdk_commercial_model || false;
  const evaluationDatasets = evaluationResult?.datasets || [];
  const evaluationBenchmarks = evaluationResult?.benchmarks || [];

  // Set initial progress values from distillation event
  const initialProgress = evaluationResult?.initial_progress || 0;
  const initialStepName = evaluationResult?.initial_step_name || "Setting up evaluation environment";
  const initialTotalSteps = evaluationResult?.initial_total_steps || 6;
  const initialCurrentStep = evaluationResult?.initial_current_step || 1;
  const distillationStatus = evaluationResult?.distillation_status || null;
  const distillationErrorDetails = evaluationResult?.distillation_error_details || null;

  const renderEvaluationStopped = (details) => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div style={{ width: '100%' }}>
        <AlertBanner
          feedback="error"
          showIcon
          text={`Evaluation stopped — ${details || "We hit an error while evaluating this version. Please choose another version to view results."}`}
          showButton={false}
          showCloseButton={false}
        />
      </div>
    </div>
  );

  const comparativeSelectedMetrics = useMemo(() => {
    if (selectedLabel === ALL_LABELS) {
      if (selectedMatchType == 'partial' || activeTab === 'relations') {
        return activeAllMetrics?.eval_general_metrics
      }
      else {
        return activeAllMetrics?.eval_general_span_level_metrics
      }
    }
    else {
      if (selectedMatchType == 'partial' || activeTab === 'relations') {
        // For relations tab in NER_WITH_RELATIONS, use relation-only category metrics
        if (localUsecase?.task_type === TaskType.NER_WITH_RELATIONS && activeTab === 'relations') {
          return activeAllMetrics?.eval_relation_only_category_metrics?.[selectedLabel]
        }
        return activeAllMetrics?.eval_class_metrics?.[selectedLabel]
      }
      else {
        return activeAllMetrics?.eval_span_level_metrics?.[selectedLabel]
      }
    }
  }, [activeAllMetrics, selectedLabel, selectedMatchType, activeTab, localUsecase?.task_type]);

  const comparativeMetrics = useMemo(() => {
    const keyMetrics = ['accuracy', 'precision', 'recall', 'f_beta_score', 'f1_score'];

    const betaCoefficient = evaluationResult?.f_score_metric_beta_coefficient;

    const formatFBetaLabel = (beta) => {
      if (beta === null || beta === undefined) {
        return 'F score';
      }
      const numeric = Number(beta);
      if (Number.isNaN(numeric)) {
        return 'F score';
      }
      const rounded = Math.round(numeric * 10) / 10;
      const display =
        Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
      return `F${display} score`;
    };

    const displayMetrics = [
      'Accuracy',
      'Precision',
      'Recall',
      formatFBetaLabel(betaCoefficient),
      'F1 score',
    ];

    const serverOverride = customThresholdMetrics
      ? (selectedLabel === ALL_LABELS
        ? customThresholdMetrics.eval_general_metrics
        : customThresholdMetrics.eval_class_metrics?.[selectedLabel])
      : null;

    return keyMetrics.reduce((results, metric) => {
      let metricData = comparativeSelectedMetrics?.[metric];
      if (!metricData) return results;

      if (serverOverride && metric in serverOverride) {
        metricData = { ...metricData, newModel: serverOverride[metric] };
      }

      return {
        ...results,
        [metric]: {
          ...metricData,
          metric: displayMetrics[keyMetrics.indexOf(metric)],
          improvement: calculateImprovement(metricData.oldModel, metricData.newModel)
        }
      };
    }, {});
  }, [comparativeSelectedMetrics, selectedLabel, customThresholdMetrics])

  const confusionMatrix = useMemo(() => {
    const base = comparativeSelectedMetrics?.confusion_matrix;
    const liveCm = customThresholdMetrics?.eval_class_metrics?.[selectedLabel]?.confusion_matrix;
    if (!liveCm) return base;
    if (!base) {
      return {
        tp: { newModel: liveCm.tp },
        fp: { newModel: liveCm.fp },
        fn: { newModel: liveCm.fn },
        tn: { newModel: liveCm.tn },
      };
    }
    return {
      tp: { ...base.tp, newModel: liveCm.tp },
      fp: { ...base.fp, newModel: liveCm.fp },
      fn: { ...base.fn, newModel: liveCm.fn },
      tn: { ...base.tn, newModel: liveCm.tn },
    };
  }, [
    comparativeSelectedMetrics?.confusion_matrix,
    customThresholdMetrics,
    selectedLabel,
  ]);

  const errorAnalysisAccuracy = useMemo(() => {
    const base = comparativeSelectedMetrics?.accuracy;
    const liveAccuracy = customThresholdMetrics?.eval_class_metrics?.[selectedLabel]?.accuracy;
    if (liveAccuracy == null) return base;
    if (base && typeof base === 'object') {
      return { ...base, newModel: liveAccuracy };
    }
    return { newModel: liveAccuracy, oldModel: base };
  }, [
    comparativeSelectedMetrics?.accuracy,
    customThresholdMetrics,
    selectedLabel,
  ]);

  // Prefer the live threshold-adjusted matrix (recomputed by
  // /compute-metrics-for-thresholds) so the confusion matrix tracks the slider
  // the same way the Quality Metrics / Samples do; fall back to the stored
  // matrix when no custom thresholds are active (e.g. while comparing models).
  const labelConfusionMatrixPayload =
    customThresholdMetrics?.eval_general_metrics?.label_confusion_matrix ||
    activeAllMetrics?.eval_general_metrics?.label_confusion_matrix ||
    null;

  const compareOptions = useMemo(() => {
    return buildCompareOptions({
      teacherModelName: teacherNameForCompare,
      versions: versionOptions,
      resultsFileName: evaluationResult?.results_file_name,
    });
  }, [teacherNameForCompare, versionOptions, evaluationResult?.results_file_name]);

  const selectedVersion = useMemo(() => {
    if (localVersion) {
      return localVersion;
    }

    if (versionOptions.length > 0) {
      return Math.max(...versionOptions);
    }

    return null;
  }, [localVersion, versionOptions]);

  // Backend map of major version -> its evaluation runs, each {version, event_id}
  // sorted by version ascending. Majors with more than one run expose the
  // "Replace Benchmark" affordance in the "To" dropdown.
  const minorRunsByMajor = evaluationResult?.minor_versions_by_major || null;
  const selectedMajorKey =
    selectedVersion != null ? String(Math.floor(Number(selectedVersion))) : null;

  // FilterBar consumes plain version numbers (major -> [5.0, 5.01, ...]).
  const minorVersionsByMajor = useMemo(() => {
    if (!minorRunsByMajor) return null;
    const map = {};
    for (const [major, runs] of Object.entries(minorRunsByMajor)) {
      map[major] = (runs || []).map((r) => Number(r.version));
    }
    return map;
  }, [minorRunsByMajor]);

  // Which run's benchmark is active per major: the session "Replace Benchmark"
  // choice wins; the currently displayed major falls back to the response's
  // selected_version (covers locks made from the Model Details drawer); other
  // majors default to their latest run.
  const activeMinorByMajor = useMemo(() => {
    if (!minorRunsByMajor) return null;
    const map = {};
    for (const [major, runs] of Object.entries(minorRunsByMajor)) {
      if (!Array.isArray(runs) || runs.length === 0) continue;
      const stored = benchmarkSelectionByMajor[major];
      if (stored && runs.some((r) => r.event_id === stored.event_id)) {
        map[major] = stored.version;
        continue;
      }
      if (
        major === selectedMajorKey &&
        evaluationResult?.selected_version != null &&
        String(Math.floor(Number(evaluationResult.selected_version))) === major
      ) {
        map[major] = evaluationResult.selected_version;
        continue;
      }
      map[major] = Math.max(...runs.map((r) => Number(r.version)));
    }
    return map;
  }, [minorRunsByMajor, benchmarkSelectionByMajor, selectedMajorKey, evaluationResult?.selected_version]);

  const replaceRuns =
    replaceBenchmarkMajor != null
      ? minorRunsByMajor?.[String(replaceBenchmarkMajor)] || []
      : [];
  // Training records are keyed on the original (root) distillation event, and
  // model-details lists that root plus every recalc child. Anchor on the
  // lowest-version run of the major (the training event), not the latest
  // recalc — fetching by a child id used to 404 the training lookup and leave
  // the Replace Benchmark modal empty.
  const replaceAnchorEventId = replaceRuns.length
    ? replaceRuns[0].event_id
    : null;
  const replaceActiveEventId = (() => {
    if (replaceBenchmarkMajor == null) return null;
    const majorKey = String(replaceBenchmarkMajor);
    const stored = benchmarkSelectionByMajor[majorKey];
    if (stored && replaceRuns.some((r) => r.event_id === stored.event_id)) {
      return stored.event_id;
    }
    if (majorKey === selectedMajorKey && evaluationResult?.selected_distillation_event_id) {
      return evaluationResult.selected_distillation_event_id;
    }
    return replaceAnchorEventId;
  })();

  // "vs …" lines in the Replace Benchmark modal name the task's current
  // teacher model (e.g. "o3 (AzureAI)") instead of the generic "Teacher".
  const replaceTeacherName = formatTeacherModelShortLabel({
    teacherModel: localUsecase?.teacher_model,
    teacherProvider: localUsecase?.teacher_provider,
  });

  const handleReplaceBenchmarkConfirm = useCallback(
    (eventId, version) => {
      if (replaceBenchmarkMajor == null || !eventId) return;
      const majorKey = String(replaceBenchmarkMajor);
      setBenchmarkSelectionByMajor((prev) => ({
        ...prev,
        [majorKey]: { event_id: eventId, version },
      }));
      setReplaceBenchmarkMajor(null);
      // Switch the Evaluation "To" view to this major, locked to the picked run.
      updateVersion(Number(replaceBenchmarkMajor), eventId);
    },
    [replaceBenchmarkMajor, updateVersion],
  );

  // Locks made from the Model Details drawer's Benchmarks tab (and "Switch to
  // latest") flow through here so the session map stays consistent with what
  // the "To" dropdown labels show.
  const handleSelectBenchmark = useCallback(
    (eventId) => {
      updateSelectedBenchmarkEventId(eventId);
      if (!selectedMajorKey) return;
      if (!eventId) {
        setBenchmarkSelectionByMajor((prev) => {
          if (!(selectedMajorKey in prev)) return prev;
          const next = { ...prev };
          delete next[selectedMajorKey];
          return next;
        });
        return;
      }
      const run = (minorRunsByMajor?.[selectedMajorKey] || []).find(
        (r) => r.event_id === eventId,
      );
      if (!run) return;
      setBenchmarkSelectionByMajor((prev) => ({
        ...prev,
        [selectedMajorKey]: { event_id: eventId, version: run.version },
      }));
    },
    [updateSelectedBenchmarkEventId, selectedMajorKey, minorRunsByMajor],
  );

  // ---- Version-vs-version Quality Metrics (three-table view) ----
  // The comparison response already carries both models evaluated on the
  // anchor (selected version's) benchmark; the first table — the compared
  // version on its OWN benchmark — needs a dedicated single-version fetch.
  const isVersionComparison = typeof currentToCompare === 'number';
  const compareMajor = isVersionComparison ? Math.floor(Number(currentToCompare)) : null;
  const compareStoredEventId =
    compareMajor != null
      ? benchmarkSelectionByMajor[String(compareMajor)]?.event_id ?? null
      : null;

  const [compareOwnEval, setCompareOwnEval] = useState(null); // { allMetrics, version }
  const [compareOwnLoading, setCompareOwnLoading] = useState(false);

  useEffect(() => {
    if (compareMajor == null || !currentUsecaseId) {
      setCompareOwnEval(null);
      setCompareOwnLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCompareOwnLoading(true);
    data_from_server_post(
      'evaluation',
      {
        client_id: clientId,
        llm_usecase_id: currentUsecaseId,
        version: compareMajor,
        comparison_mode: false,
        compare_with_version: null,
        sub_task_type:
          localUsecase?.task_type !== TaskType.NER_WITH_RELATIONS
            ? null
            : activeTab === 'entities' ? SubTaskType.Ner : SubTaskType.Relations,
        selected_distillation_event_id: compareStoredEventId,
      },
      (result) => {
        if (cancelled) return;
        setCompareOwnLoading(false);
        setCompareOwnEval(
          result?.success
            ? {
              allMetrics: result?.all_metrics || null,
              version: result?.selected_version ?? null,
            }
            : null,
        );
      },
      () => {
        if (cancelled) return;
        setCompareOwnLoading(false);
        setCompareOwnEval(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [compareMajor, currentUsecaseId, activeTab, localUsecase?.task_type, compareStoredEventId]);

  // Scope the compared version's own-benchmark metrics the same way the main
  // comparison metrics are scoped (language / label / match type / NER tab).
  const compareOwnBenchmarkValues = useMemo(() => {
    const all = compareOwnEval?.allMetrics;
    if (!all) return null;
    const active =
      selectedLanguage && all?.by_language?.[selectedLanguage]
        ? all.by_language[selectedLanguage]
        : all;
    let scoped;
    if (selectedLabel === ALL_LABELS) {
      scoped = selectedMatchType === 'partial' || activeTab === 'relations'
        ? active?.eval_general_metrics
        : active?.eval_general_span_level_metrics;
    } else if (selectedMatchType === 'partial' || activeTab === 'relations') {
      scoped = localUsecase?.task_type === TaskType.NER_WITH_RELATIONS && activeTab === 'relations'
        ? active?.eval_relation_only_category_metrics?.[selectedLabel]
        : active?.eval_class_metrics?.[selectedLabel];
    } else {
      scoped = active?.eval_span_level_metrics?.[selectedLabel];
    }
    if (!scoped) return null;
    const values = {};
    for (const key of ['accuracy', 'precision', 'recall', 'f_beta_score', 'f1_score']) {
      const v = scoped?.[key]?.newModel;
      if (v != null) values[key] = v;
    }
    return values;
  }, [compareOwnEval, selectedLanguage, selectedLabel, selectedMatchType, activeTab, localUsecase?.task_type]);

  const versionComparisonLabels = useMemo(() => {
    if (compareMajor == null) return null;
    const compareMinor =
      activeMinorByMajor?.[String(compareMajor)]
      ?? compareOwnEval?.version
      ?? compareMajor;
    const anchorMinor = evaluationResult?.selected_version ?? selectedVersion;
    const selectedMajor =
      selectedVersion != null ? Math.floor(Number(selectedVersion)) : null;
    return {
      compareVersionLabel: `V${compareMajor}`,
      compareBenchmarkLabel: `V${formatMinorVersionLabel(compareMinor)} benchmark`,
      compareModelLabel: `V${compareMajor} Model`,
      anchorVersionLabel: `V${formatMinorVersionLabel(anchorMinor)}`,
      anchorBenchmarkLabel: `V${formatMinorVersionLabel(anchorMinor)} benchmark`,
      anchorModelLabel: selectedMajor != null ? `V${selectedMajor} Model` : 'Model',
    };
  }, [compareMajor, activeMinorByMajor, compareOwnEval?.version, evaluationResult?.selected_version, selectedVersion]);

  // ---- Versions Diff modal (version-vs-version only) ----
  // Diffs the immutable train-set snapshots of the two compared versions. The
  // training snapshot lives on each major's ROOT run (runs[0].event_id in
  // minor_versions_by_major) — recalc children are benchmark re-evaluations.
  const [versionsDiffOpen, setVersionsDiffOpen] = useState(false);
  const versionsDiffEventIds = useMemo(() => {
    if (compareMajor == null || !minorRunsByMajor || !selectedMajorKey) return null;
    const fromEventId = minorRunsByMajor[String(compareMajor)]?.[0]?.event_id ?? null;
    const toEventId = minorRunsByMajor[selectedMajorKey]?.[0]?.event_id ?? null;
    if (!fromEventId || !toEventId || fromEventId === toEventId) return null;
    return { fromEventId, toEventId };
  }, [compareMajor, minorRunsByMajor, selectedMajorKey]);

  // Teacher / no-comparison Quality Metrics: gray caption is the major version,
  // with the selected run's benchmark and major-version model labels underneath
  // (same chrome as version-vs-version, without the extra own-benchmark table).
  const selectedVersionHeaderLabels = useMemo(() => {
    const major = selectedVersion != null ? Math.floor(Number(selectedVersion)) : null;
    if (major == null || !Number.isFinite(major)) return null;
    const minor =
      evaluationResult?.selected_version
      ?? activeMinorByMajor?.[String(major)]
      ?? selectedVersion;
    const minorLabel = formatMinorVersionLabel(minor) ?? `${major}.0`;
    return {
      versionLabel: `V${major}`,
      benchmarkLabel: `V${minorLabel} benchmark`,
      modelLabel: `V${major} Model`,
    };
  }, [selectedVersion, evaluationResult?.selected_version, activeMinorByMajor]);

  const debouncedFetchMetrics = useCallback(
    debounce((thresholds) => {
      data_from_server_post(
        'compute-metrics-for-thresholds',
        {
          client_id: clientId,
          llm_usecase_id: currentUsecaseId,
          version: selectedVersion,
          custom_thresholds: thresholds,
          selected_distillation_event_id: selectedBenchmarkEventId || null,
        },
        (response) => {
          setCustomThresholdLoading(false);
          if (response.success && response.metrics) {
            setCustomThresholdMetrics(response.metrics);
          } else {
            setCustomThresholdMetrics(null);
          }
        },
        () => {
          setCustomThresholdLoading(false);
          setCustomThresholdMetrics(null);
        }
      );
    }, 500),
    [currentUsecaseId, selectedVersion, selectedBenchmarkEventId]
  );

  useEffect(() => {
    return () => debouncedFetchMetrics.cancel();
  }, [debouncedFetchMetrics]);

  useEffect(() => {
    if (!liveThresholds || !liveMetricsBaseline) {
      setCustomThresholdMetrics(null);
      setCustomThresholdLoading(false);
      return;
    }
    const isDirty = Object.keys(liveMetricsBaseline).some(
      label => liveThresholds[label] !== liveMetricsBaseline[label]
    );
    if (!isDirty) {
      setCustomThresholdMetrics(null);
      setCustomThresholdLoading(false);
      debouncedFetchMetrics.cancel();
      return;
    }
    // Flag loading before the debounce delay so consumers (e.g. the Quality
    // Metrics deltas) can show a pending indicator as soon as the slider moves.
    setCustomThresholdLoading(true);
    debouncedFetchMetrics(liveThresholds);
  }, [liveThresholds, liveMetricsBaseline, debouncedFetchMetrics]);

  const debouncedSetTableThresholds = useCallback(
    debounce((val) => setDebouncedThresholdsForTable(val), 600),
    []
  );
  useEffect(() => {
    debouncedSetTableThresholds(liveThresholds);
    return () => debouncedSetTableThresholds.cancel();
  }, [liveThresholds, debouncedSetTableThresholds]);

  // Adding a model for comparison hides the Threshold panel, so any unsaved
  // slider adjustments must be dropped here. Otherwise the stale, unsaved
  // overrides keep flowing into the comparison metrics (customThresholdMetrics)
  // and the sample rows (selected_thresholds_override), making the two versions
  // compare against per-version thresholds the user never saved.
  useEffect(() => {
    if (!currentToCompare) return;
    debouncedFetchMetrics.cancel();
    debouncedSetTableThresholds.cancel();
    setLiveThresholds(null);
    setDebouncedThresholdsForTable(null);
    setCustomThresholdMetrics(null);
    setCustomThresholdLoading(false);
  }, [currentToCompare, debouncedFetchMetrics, debouncedSetTableThresholds]);

  // Disallow comparing a version to itself (SpecificAI vs the same SpecificAI version).
  // If the user changes either Select such that they're equal, clear Compare.
  useEffect(() => {
    if (selectedVersion == null) return;
    if (typeof versionToCompare !== 'number') return;

    const toMajor = Math.floor(Number(selectedVersion));
    const compareMajor = Math.floor(Number(versionToCompare));
    if (!Number.isFinite(toMajor) || !Number.isFinite(compareMajor)) return;

    if (toMajor === compareMajor) {
      setChoseCurrent(false);
      updateVersionToCompare(null);
    }
  }, [selectedVersion, versionToCompare, updateVersionToCompare]);

  const activeEvaluationProgress = useMemo(() => {
    if (!localUsecase?._id || !selectedVersion) return null;

    const exact = evaluationProgress?.[`${localUsecase._id}_${selectedVersion}`];
    if (exact) return exact;

    // If the UI is currently pointing at a major version (e.g. 4) but evaluation updates
    // arrive for a minor version (e.g. 4.01), surface the latest minor under that major.
    if (!Number.isInteger(Number(selectedVersion))) return null;
    const major = Math.floor(Number(selectedVersion));
    const candidates = Object.values(evaluationProgress || {}).filter((p) => {
      const v = Number(p?.version);
      return (
        p?.usecase_id === localUsecase._id &&
        Number.isFinite(v) &&
        Math.floor(v) === major
      );
    });
    if (candidates.length === 0) return null;

    // If user just triggered a new recalculation, only show progress entries
    // that arrived AFTER the baseline (i.e. from the new run, not the old one).
    if (
      optimisticRecalc &&
      optimisticRecalc.major === major &&
      typeof optimisticRecalc.baseline_minor === "number"
    ) {
      const newer = candidates
        .filter((p) => Number(p?.version) > optimisticRecalc.baseline_minor)
        .reduce(
          (best, cur) =>
            best == null || Number(cur?.version) > Number(best?.version) ? cur : best,
          null
        );
      return newer; // null keeps UI in optimistic "step 1/6" state
    }

    const latest = candidates.reduce((best, cur) =>
      Number(cur?.version) > Number(best?.version) ? cur : best
    );

    return latest;
  }, [evaluationProgress, localUsecase?._id, selectedVersion, optimisticRecalc]);

  // Clear optimistic state once real WS progress arrives (or after terminal state).
  useEffect(() => {
    if (!optimisticRecalc) return;
    if (!selectedVersion) return;
    const major = Math.floor(Number(selectedVersion));
    if (major !== optimisticRecalc.major) return;
    if (!activeEvaluationProgress) return;
    const status = activeEvaluationProgress?.status;

    if (status === "completed" || status === "failed") {
      setOptimisticRecalc(null);
      return;
    }

    // Clear optimistic state when we see a newer minor version.
    const v = Number(activeEvaluationProgress?.version);
    if (Number.isFinite(v) && v > (optimisticRecalc.baseline_minor ?? 0)) {
      setOptimisticRecalc(null);
    }
  }, [optimisticRecalc, activeEvaluationProgress, selectedVersion]);

  const handleRefreshStart = useCallback(() => {
    if (!selectedVersion) return;
    const major = Math.floor(Number(selectedVersion));
    const usecaseId = localUsecase?._id;

    // A new evaluation run supersedes any session "Replace Benchmark" lock for
    // this major — otherwise the auto-refresh would keep showing the old run.
    setBenchmarkSelectionByMajor((prev) => {
      const majorKey = String(major);
      if (!(majorKey in prev)) return prev;
      const next = { ...prev };
      delete next[majorKey];
      return next;
    });

    // Snapshot the latest known minor for this major so we can ignore it while waiting for the next run.
    const candidates = Object.values(evaluationProgress || {}).filter((p) => {
      const v = Number(p?.version);
      return (
        p?.usecase_id === usecaseId &&
        Number.isFinite(v) &&
        Math.floor(v) === major
      );
    });
    const latestMinor = candidates.length
      ? candidates.reduce((best, cur) =>
        Number(cur?.version) > Number(best?.version) ? cur : best
      )?.version
      : major;

    // Remove stale progress entries for this usecase+major so the UI never briefly
    // shows the previous run's terminal state (e.g. step 6/6) while waiting for the
    // new run's first WS update.
    if (candidates.length > 0) {
      setEvaluationProgress((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          const entry = next[key];
          if (entry?.usecase_id !== usecaseId) continue;
          const v = Number(entry?.version);
          if (Number.isFinite(v) && Math.floor(v) === major) {
            delete next[key];
          }
        }
        return next;
      });
    }

    setOptimisticRecalc({
      major,
      baseline_minor: Number(latestMinor) || major,
    });
  }, [selectedVersion, evaluationProgress, localUsecase?._id, setEvaluationProgress]);

  const handleRefreshComplete = useCallback((result) => {
    // If trigger failed, don't keep the optimistic processing state.
    if (!result?.success) {
      setOptimisticRecalc(null);
      return;
    }
    // A realtime resync looks this run up when every update for it was missed.
    const run = recalculationRunFromResponse(result.response);
    if (run) setOptimisticRecalc((prev) => (prev ? { ...prev, run } : prev));
  }, []);

  const shouldShowProcessingOverlay = useMemo(() => {
    if (!selectedVersion) return false;
    // If the user explicitly locked the page to a specific historical benchmark
    // via the Model Details drawer's Benchmarks tab, respect that selection and
    // do not hide the historical results behind the "Evaluating your model..."
    // overlay for an unrelated in-progress recalc on the same major version.
    const viewingHistoricalRun =
      !!selectedBenchmarkEventId &&
      !!evaluationResult?.success &&
      evaluationResult?.selected_distillation_event_id === selectedBenchmarkEventId &&
      evaluationResult?.is_latest_evaluation === false;
    if (viewingHistoricalRun) return false;
    const major = Math.floor(Number(selectedVersion));
    const optimisticForSelectedMajor =
      optimisticRecalc != null && optimisticRecalc.major === major;
    const wsInProgressForSelectedMajor =
      !!activeEvaluationProgress && activeEvaluationProgress.status !== "completed";
    return optimisticForSelectedMajor || wsInProgressForSelectedMajor;
  }, [optimisticRecalc, selectedVersion, activeEvaluationProgress, selectedBenchmarkEventId, evaluationResult]);

  const showResultsView =
    !loading && evaluationExists && !studentModelProcessing && !shouldShowProcessingOverlay;

  // todo - add shouldShowProcessingOverlay to the FilterBar


  const [viewMode, setViewMode] = useState('table');

  // Determine the comparison model name based on compareWith value
  const getComparisonModelName = () => {
    if (!currentToCompare) return null;

    if (currentToCompare === 'oldModel') {
      return teacherNameForCompare;
    } else if (currentToCompare === 'file') {
      return evaluationResult?.results_file_name;
    } else if (typeof currentToCompare === 'number') {
      return `SpecificAI v${currentToCompare}`;
    }

    return 'Comparison Model';
  };

  // Resolve the Performance tab's base models for both the "To:" and
  // "Compare:" selects:
  //   - "To:"     -> use the version -> base_model_name map returned by
  //                  /evaluation, keyed by the floored selected version.
  //   - "Compare:"
  //       numeric version  -> same map, keyed by the compared version.
  //       'oldModel'       -> the evaluated teacher (stored `evaluated_teacher_model_name` /
  //                           processing fallback; not necessarily current usecase teacher). The
  //                           Performance tab fetches `performance_metrics` for that base name.
  //       'file' / other   -> null (results-file comparison has no metrics).
  const versionToBaseModel = evaluationResult?.version_to_base_model_name || {};
  const selectedMajorVersionKey =
    selectedVersion != null ? String(Math.floor(Number(selectedVersion))) : null;
  const selectedBaseModelName = selectedMajorVersionKey
    ? versionToBaseModel[selectedMajorVersionKey] || null
    : null;

  let comparisonBaseModelName = null;
  let comparisonModelLabelForPerformance = null;
  // Performance fetch needs to know whether the comparison points at a
  // student (SpecificAI version) or a teacher doc — the two have a
  // different shape in `performance_metrics` (teachers have no infra,
  // so the route looks them up by (model, kind, concurrency, length)).
  const comparisonModelKind =
    currentToCompare === 'oldModel' ? 'teacher' : 'student';
  if (typeof currentToCompare === 'number') {
    comparisonBaseModelName =
      versionToBaseModel[String(Math.floor(Number(currentToCompare)))] || null;
    comparisonModelLabelForPerformance = comparisonBaseModelName
      ? `SpecificAI v${currentToCompare}`
      : null;
  } else if (currentToCompare === 'oldModel' && teacherNameForCompare) {
    comparisonBaseModelName = teacherNameForCompare;
    comparisonModelLabelForPerformance = teacherNameForCompare;
  }

  useEffect(() => {
    if (localUsecase) {
      data_from_server_post('get-display-names', localUsecase._id, (response) => {
        setDisplayNames(response || {});
      });
    }
  }, [localUsecase]);

  // Customize dropdown rendering with display names (and optional counts for the filter dropdown)
  const dropdownLabelRender = (label, count) => {
    const hasDisplayName = displayNames[label] && displayNames[label] !== label;

    if (!hasDisplayName) {
      return (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <TruncatedText
              text={String(label)}
              maxWidth={260}
              zIndex={6000}
            />
          </div>
          {typeof count === "number" && Number.isFinite(count) && label !== ALL_LABELS && (
            <span style={{ color: "#6B7280", fontSize: 12, marginLeft: 10, flex: "0 0 auto" }}>
              {count.toLocaleString()}
            </span>
          )}
        </div>
      );
    }

    // Pick a color based on the label - we'll use consistent colors similar to the screenshot
    let bgColor = getPredictionColor(label, evaluationResult?.classes) || '#f97316'; // Default orange for "Numeric"
    if (displayNames[label].toLowerCase().includes('description')) {
      bgColor = '#3b82f6'; // Blue for "Description"
    } else if (displayNames[label].toLowerCase().includes('entity')) {
      bgColor = '#10b981'; // Green for "Entity"
    }

    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 8 }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <TruncatedText
            text={String(label)}
            maxWidth={220}
            zIndex={6000}
          />
        </div>
        <div
          style={{
            flex: "0 0 auto",
            background: bgColor,
            color: "white",
            fontSize: "14px",
            padding: "2px 12px",
            borderRadius: "16px",
            boxShadow: `0 2px 8px ${bgColor}70`,
            display: "inline-flex",
            alignItems: "center",
            lineHeight: "1.2",
            fontWeight: "bold",
          }}
        >
          <TruncatedText
            text={String(displayNames[label])}
            maxWidth={220}
            zIndex={6000}
            style={{ color: "white" }}
          />
        </div>
        {typeof count === "number" && Number.isFinite(count) && label !== ALL_LABELS && (
          <span style={{ color: "#6B7280", fontSize: 12, marginLeft: 2, flex: "0 0 auto" }}>
            {count.toLocaleString()}
          </span>
        )}
      </div>
    );
  };


  const shouldShowDegradationAlert = () => {
    if (!evaluationExists) return false;
    if (loading) return false;
    if (teacherModelProcessing || studentModelProcessing) return false;
    if (!comparativeMetrics || !currentToCompare) return false;

    const generalMetrics = comparativeMetrics;
    if (!generalMetrics) return false;

    const keyMetrics = ['Accuracy', 'Precision', 'Recall', 'F1 Score'];
    const degradedMetrics = Object.entries(generalMetrics)
      .filter(([metric, value]) =>
        keyMetrics.includes(metric)
        && parseFloat(value.improvement) < 0);

    return degradedMetrics.length >= 3;
  };

  // Generate class options for the dropdown
  const getClassOptions = () => {
    const options = [{
      label: localUsecase?.task_type === TaskType.NER || (localUsecase?.task_type === TaskType.NER_WITH_RELATIONS && activeTab === "entities") ? 'All entity types' : (localUsecase?.task_type === TaskType.NER_WITH_RELATIONS && activeTab === "relations") ? 'All relation types' : 'All Labels',
      value: ALL_LABELS
    }];

    // Create a sorted copy of the labels array
    const sortedLabels = [...labels].sort((a, b) => {
      // If we don't have benchmark counts (older events), sort alphabetically only.
      if (!labelTotals) {
        return String(a).localeCompare(String(b));
      }

      const aCount = labelTotals?.[a];
      const bCount = labelTotals?.[b];
      const aHas = typeof aCount === "number" && Number.isFinite(aCount);
      const bHas = typeof bCount === "number" && Number.isFinite(bCount);

      // Put labels *with* counts above labels *without* counts.
      if (aHas !== bHas) return aHas ? -1 : 1;

      // Primary: count (desc)
      if (aHas && bHas && bCount !== aCount) return bCount - aCount;

      // Secondary: alphabetical
      return String(a).localeCompare(String(b));
    });

    // Add sorted labels to options
    sortedLabels.forEach(label => {
      options.push({ label, value: label, count: labelTotals?.[label] });
    });

    if (isNerEntitiesThresholdContext(localUsecase?.task_type, activeTab)) {
      Object.keys(evaluationResult?.threshold_candidates?.calibrated || {}).forEach((key) => {
        if (!options.some((opt) => opt.value === key)) {
          options.push({ label: key, value: key, count: labelTotals?.[key] });
        }
      });
    }

    return options;
  };

  // "Adjust Threshold" quick action: jumps straight to a per-label threshold
  // view (Compare: None + first tunable label from the Labels dropdown) so the
  // Threshold panel cards become visible. Classification still requires
  // persisted T + candidates. NER offers the same interaction from entity
  // types even when those fields were never written (live metrics via
  // /compute-metrics-for-thresholds).
  const playWithThresholdLabel = resolvePlayWithThresholdLabel({
    taskType: localUsecase?.task_type,
    activeTab,
    isMultilabel: !!localUsecase?.is_multilabel,
    evaluationResult,
    classOptions: getClassOptions(),
    allLabelsValue: ALL_LABELS,
  });

  const handlePlayWithThreshold = () => {
    if (!playWithThresholdLabel) return;
    if (thresholdModeSnapshot == null) {
      setThresholdModeSnapshot({
        currentToCompare,
        selectedVersion,
        selectedLabel,
        selectedLanguage,
        selectedMatchType,
        viewTab,
      });
    }
    // The Threshold panel only renders on the Overview / Samples views.
    if (viewTab === EVAL_VIEW_TABS.PERFORMANCE) {
      setViewTab(EVAL_VIEW_TABS.OVERVIEW);
    }
    // Only clear Compare when it's actually set; setCurrentToCompare(null)
    // re-queries the evaluation, which is wasteful if nothing changed.
    if (currentToCompare != null) {
      setCurrentToCompare(null);
    }
    setSelectedLabel(playWithThresholdLabel);
  };

  // Back button of the "Adjust Threshold" mode: restore the filters captured
  // just before the mode was first entered, then leave the mode.
  const handleExitThresholdMode = () => {
    if (!thresholdModeSnapshot) return;
    const snapshot = thresholdModeSnapshot;
    setThresholdModeSnapshot(null);
    // setCurrentToCompare re-queries the evaluation, so only call it when the
    // value actually changed.
    if (snapshot.currentToCompare !== currentToCompare) {
      setCurrentToCompare(snapshot.currentToCompare);
    }
    if (snapshot.selectedVersion !== selectedVersion) {
      setSelectedVersion(snapshot.selectedVersion);
    }
    setSelectedLabel(snapshot.selectedLabel);
    setSelectedLanguage(snapshot.selectedLanguage);
    setSelectedMatchType(snapshot.selectedMatchType);
    setViewTab(snapshot.viewTab);
  };

  const distillationEventId = evaluationResult?.distillation_event_id || null;
  const displayedDistillationEventId =
    evaluationResult?.selected_distillation_event_id || distillationEventId;
  // No evaluated version yet: show "Crunching the numbers" while a training
  // is still running for this usecase, otherwise the plain empty state.
  const showTrainingInProgressEmptyState = isTrainingInProgress({
    trainStatuses,
    usecaseId: currentUsecaseId,
    usecaseStatus: localUsecase?.status,
    distillationEventId,
    evaluationResult,
  });
  const teacherEvaluationStatus = teacherStatusForDisplayedRun(
    localUsecase?.last_teacher_evaluation_status,
    displayedDistillationEventId
  );

  const modelDetailsDrawerVersion = useMemo(
    () =>
      buildModelDrawerVersionFromEvaluation({
        localUsecase,
        currentUsecaseId,
        selectedVersion,
        distillationEventId,
        evaluationResult,
        trainStatuses,
        evaluationResultsVisible: showResultsView,
        defaultClientId: DEFAULT_CLIENT_ID,
      }),
    [
      localUsecase,
      currentUsecaseId,
      selectedVersion,
      distillationEventId,
      evaluationResult,
      trainStatuses,
      showResultsView,
    ],
  );

  // Download / Deploy from the drawer mirror the Models page ("More actions"):
  // Download opens the format picker (raw files / Docker / ECR) and then the
  // Configure Deployment modal; the submit reports progress through
  // ``setProgressStatus`` so the global FloatingProgressBar reflects it.
  const loadTrainingsForConfigure = useCallback(
    () =>
      fetchTrainingsForConfigureModal({
        allUsecases,
        clientId: localUsecase?.client_id || DEFAULT_CLIENT_ID,
      }),
    [allUsecases, localUsecase?.client_id],
  );
  // Optimistically flip the usecase to "Deploy in Progress" the moment the
  // request is accepted, mirroring the Models page / Homepage. Refetching
  // usecases here instead races the backend status flip (and can piggyback a
  // stale in-flight request), leaving the drawer without a loading state; the
  // completion websocket (`updateStatus` action) delivers the final status.
  const handleDeploySuccess = useCallback(
    (training) => {
      const usecaseId = training?.llm_usecase_id || currentUsecaseId;
      if (usecaseId) updateStatus(usecaseId, deploy_in_progress);
    },
    [updateStatus, currentUsecaseId],
  );
  const configureFlow = useConfigureDeploymentFlow({
    setProgressStatus,
    onDeploySuccess: handleDeploySuccess,
    loadTrainings: loadTrainingsForConfigure,
  });
  const handleModelDrawerDownload = configureFlow.handleDownloadTraining;
  const handleModelDrawerBenchmarkDownload = useCallback(
    (training) => modelDrawerDownloadBenchmark(training),
    [],
  );
  const handleModelDrawerDeploy = useCallback(
    async (training) => {
      try {
        const { mode, sharedConfig, tasks } = buildDefaultDeployPayload(
          training,
          localUsecase,
        );
        await submitConfigureDeployment({
          mode,
          sharedConfig,
          tasks,
          setProgressStatus,
          onDeploySuccess: handleDeploySuccess,
        });
      } catch (err) {
        sparkToast.error('Deployment Failed', {
          content: err?.message || 'Request failed',
          duration: 6000,
        });
      }
    },
    [localUsecase, setProgressStatus, handleDeploySuccess],
  );
  const handleModelDrawerUndeploy = useCallback(
    (training) =>
      modelDrawerUndeploy(training, (t) => {
        const usecaseId = t?.llm_usecase_id || currentUsecaseId;
        if (usecaseId) updateStatus(usecaseId, undeploy_in_progress);
      }),
    [updateStatus, currentUsecaseId],
  );

  const handleGoToModel = useCallback(() => {
    if (distillationEventId) {
      setModelDetailsOpen(true);
    }
  }, [distillationEventId]);

  const isNerWithRelations = localUsecase?.task_type === TaskType.NER_WITH_RELATIONS;
  const isNer = localUsecase?.task_type === TaskType.NER;
  const isClassification = localUsecase?.task_type === TaskType.classification;
  // Keep the Overview / Samples / Performance rail visible while an evaluation
  // is loading or re-running (e.g. right after "Evaluate New Version"), so the
  // processing/loading card keeps the same layout and paddings as the results
  // view. Only the true empty state (no versions at all) renders without it.
  const hasEvaluationVersions = (versionOptions || []).length > 0;
  const showEvaluationViewTabs =
    !isNerWithRelations && (showResultsView || hasEvaluationVersions);
  const isPerformanceView = showEvaluationViewTabs && viewTab === EVAL_VIEW_TABS.PERFORMANCE;
  const isSamplesView = showEvaluationViewTabs && viewTab === EVAL_VIEW_TABS.SAMPLES;
  const isOverviewView = showEvaluationViewTabs && viewTab === EVAL_VIEW_TABS.OVERVIEW;

  // The ThresholdPanel renders its own Quality Metrics card, so when it is shown
  // we hide the standalone overview Quality Metrics / Metrics chart below it to
  // avoid duplicating the same card.
  //
  // The rejection label (configured on the current version's distillation event)
  // is the "no confident prediction" fallback class, so tuning a threshold for it
  // is meaningless. When it is the selected label we hide the whole Threshold
  // panel and fall back to the standalone Quality Metrics card.
  const isRejectionLabelSelected =
    evaluationResult?.rejection_label_name != null &&
    selectedLabel === evaluationResult.rejection_label_name;
  // Whether the standalone Threshold card is applicable for this label in a
  // single-version (non-comparison) view. Classification still requires
  // calibration T plus multilabel or confidence rejection. NER entities only
  // need a specific entity type selected.
  // Classification: multilabel or confidence-rejection, non-rejection label, and T.
  // NER entities: per-entity-type thresholds (drop span below floor); no
  // rejection label and no requirement that T was persisted.
  const thresholdCardApplicable =
    selectedLabel !== ALL_LABELS &&
    !isRejectionLabelSelected &&
    ((localUsecase?.task_type === TaskType.classification &&
      evaluationResult?.calibration_temperature != null &&
      (localUsecase?.is_multilabel ||
        evaluationResult?.confidence_rejection_enabled)) ||
      isNerEntitiesContext);

  // When comparing two versions ("Compare:"/"To:") on a specific label, threshold
  // tuning is not meaningful, so we hide the whole Threshold panel (Threshold card,
  // Precision-Recall Trade-off, Current/Optimal threshold cards) and fall back to
  // the standalone Quality Metrics comparison table (version vs version + improvement).
  const showThresholdPanel = thresholdCardApplicable && !currentToCompare;

  return (
    <div>
      <ImproveFlowModal
        open={improveOpen}
        onClose={() => setImproveOpen(false)}
        usecase={localUsecase}
      />
      {/* TABS FOR NER_WITH_RELATIONS */}
      {isNerWithRelations && (
        <TabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
      {/* OVERVIEW / SAMPLES / PERFORMANCE side-rail tabs for non-NER_WITH_RELATIONS
          task types (same shared layout as the Data Editor). When hidden, the
          children below render directly without the rail. */}
      <EvaluationViewTabs show={showEvaluationViewTabs} activeTab={viewTab} onChange={setViewTab}>
      {/* FILTERS AND VIEW TOGGLE BAR */}
      {showResultsView && (
        <FilterBar
          compareOptions={compareOptions}
          versionOptions={versionOptions.map(v => ({ label: `v${v}`, value: v }))}
          versionOptionsRaw={versionOptions}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          currentToCompare={currentToCompare}
          setCurrentToCompare={setCurrentToCompare}
          selectedLabel={selectedLabel}
          setSelectedLabel={setSelectedLabel}
          languageOptions={languageOptions}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          selectedMatchType={selectedMatchType}
          setSelectedMatchType={setSelectedMatchType}
          getClassOptions={getClassOptions}
          dropdownLabelRender={dropdownLabelRender}
          localUsecase={localUsecase}
          viewMode={viewMode}
          setViewMode={setViewMode}
          moveToScreen={moveToScreen}
          onImproveClick={() => setImproveOpen(true)}
          showImproveEvals={showImproveEvals}
          taskType={localUsecase?.task_type}
          activeTab={activeTab}
          onRefreshStart={handleRefreshStart}
          evaluationProgress={activeEvaluationProgress}
          onRetrain={onRetrainClick}
          onRefreshComplete={handleRefreshComplete}
          distillationEventId={distillationEventId}
          onGoToModel={distillationEventId ? handleGoToModel : undefined}
          onPlayWithThreshold={playWithThresholdLabel ? handlePlayWithThreshold : undefined}
          isThresholdMode={thresholdModeSnapshot != null}
          onBackFromThreshold={handleExitThresholdMode}
          hideLabelSelect={isPerformanceView}
          hideViewToggle={isPerformanceView}
          hideLanguageSelect={isPerformanceView}
          runDateIso={evaluationResult?.selected_event_created_datetime}
          isLatestEvaluation={evaluationResult?.is_latest_evaluation}
          runStatusVersion={evaluationResult?.selected_version ?? selectedVersion}
          onSwitchToLatest={() => handleSelectBenchmark(null)}
          minorVersionsByMajor={minorVersionsByMajor}
          activeMinorByMajor={activeMinorByMajor}
          onReplaceBenchmark={setReplaceBenchmarkMajor}
        />
      )}
      {showResultsView &&
        currentToCompare === "oldModel" &&
        teacherModelFailed &&
        !showEvaluationErrorsAlert && (
        <div style={{ marginBottom: 16 }}>
          <ErrorDetailsAlert
            title="Teacher evaluation failed"
            message="Teacher evaluation failed. Teacher-side metrics and comparisons may be missing or stale."
            details={teacherModelErrorDetails || teacherModelError}
          />
        </div>
      )}
      {showResultsView && (
        <UnsupportedLanguagesWarning
          unsupportedLanguages={evaluationResult?.unsupported_languages}
        />
      )}
      {shouldShowDegradationAlert() && (
        <div style={{ marginBottom: 16 }}>
          <AlertBanner
            feedback="warning"
            showIcon
            text="Performance Degradation Detected — The new model is showing decreased performance in multiple metrics. To improve results, consider adding more training data and reviewing the data labels in the 'Data Selection' page."
            showButton
            buttonLabel="Go to Data Selection"
            onActionClick={() => moveToScreen(localUsecase?._id, "data-selection")}
            showCloseButton
          />
        </div>
      )}
      {showResultsView && (
        <IncompleteTrainingAlert
          show={hasIncompleteTrainings}
          version={localUsecase?.version}
        />
      )}
      {/* todo - check if this is needed */}
      {/* {currentToCompare === 'oldModel' && all_cached_from_sdk_commercial_model && (
        <Alert
          showIcon
          icon={<InfoCircleOutlined />}
          type="info"
          message="Teacher Model Comparison Notice"
          description="When evaluating against the same teacher model used during data recording, the resulting metrics reflect a comparison with the model itself rather than an actual ground truth. In this scenario, perfect scores (100%) are expected, as the model is effectively being compared to its own outputs."
          style={{ marginBottom: '16px' }}
          closable
        />
      )} */}
      {/* Main content: show only one of DetailedMetrics or MetricsChart based on viewMode */}
      {showResultsView && isPerformanceView && (
        <div style={{ marginTop: 8 }}>
          <PerformanceTab
            baseModelName={selectedBaseModelName}
            benchmarkNames={evaluationResult?.benchmarks}
            clientId={localUsecase?.client_id}
            comparisonBaseModelName={comparisonBaseModelName}
            comparisonModelKind={comparisonModelKind}
            comparisonModelLabel={comparisonModelLabelForPerformance}
            costConfig={performanceCostConfig}
            currentModelLabel={`SpecificAI v${selectedVersion}`}
            filters={performanceFilters}
            llmUsecaseId={currentUsecaseId}
            modelVersion={selectedVersion}
            onCostConfigChange={setPerformanceCostConfig}
            onFiltersChange={setPerformanceFilters}
            taskType={localUsecase?.task_type}
          />
        </div>
      )}
      {showResultsView && !isPerformanceView && (
        <div>
          {/* Indicative banner shown above the Quality Metrics card when the
              evaluation metrics record (SpecificAI v{i} and/or teacher model)
              has recorded errors during evaluation. */}
          <EvaluationErrorsAlert
            evaluationErrors={evaluationErrors}
            teacherEvaluationErrors={teacherEvaluationErrors}
            currentVersion={`v${selectedVersion}`}
            teacherModelName={teacherNameForCompare}
            style={{ marginBottom: 16 }}
          />
          {/* Threshold adjustment is gated to a specific selected label (hidden on
              the "All Labels" view) for both multi-label and single-label. For
              single-label it additionally requires the confidence-threshold
              fallback toggle to be enabled. */}
          {showThresholdPanel && (
              <ThresholdPanel
                llmUsecaseId={currentUsecaseId}
                version={selectedVersion}
                calibrationTemperature={
                  evaluationResult?.calibration_temperature ??
                  (isNerEntitiesContext ? 1.0 : null)
                }
                selectedThresholdConfig={evaluationResult?.selected_threshold_config}
                selectedThresholds={evaluationResult?.selected_thresholds}
                thresholdCandidates={
                  isNerEntitiesContext
                    ? mergeNerThresholdCandidates(
                        evaluationResult?.threshold_candidates,
                        labels,
                        { selectedThresholds: evaluationResult?.selected_thresholds },
                      )
                    : evaluationResult?.threshold_candidates
                }
                baselineThresholds={liveMetricsBaseline}
                beta={evaluationResult?.f_score_metric_beta_coefficient}
                allMetrics={activeAllMetrics}
                customThresholdMetrics={customThresholdMetrics}
                customThresholdLoading={customThresholdLoading}
                onThresholdSaved={refresh}
                onLiveThresholdsChange={setLiveThresholds}
                isMultilabel={!!localUsecase?.is_multilabel}
                selectedLabel={selectedLabel}
                allLabels={labels}
                thresholdOnly={isSamplesView}
                // NER entity candidates carry tn=0 by convention, so FPR is undefined there.
                showFalsePositiveRate={!isNerEntitiesContext}
              />
            )}
          {/* OVERVIEW: Quality metrics + matrix/pies. Hidden when on Samples tab. */}
          {(!showEvaluationViewTabs || isOverviewView) && (
            <>
              {!showThresholdPanel && (viewMode === 'table' ? (
                isVersionComparison && versionComparisonLabels ? (
                  <QualityMetricsVersionComparison
                    metrics={comparativeMetrics}
                    ownBenchmarkValues={compareOwnBenchmarkValues}
                    ownBenchmarkLoading={compareOwnLoading}
                    {...versionComparisonLabels}
                    onViewVersionsDiff={
                      versionsDiffEventIds ? () => setVersionsDiffOpen(true) : null
                    }
                  />
                ) : (
                <QualityMetrics
                  metrics={comparativeMetrics}
                  teacherModelProcessing={teacherModelProcessing}
                  teacherModelFailed={teacherModelFailed}
                  teacherModelErrorDetails={teacherModelErrorDetails || teacherModelError}
                  studentModelProcessing={studentModelProcessing}
                  teacherEvaluationStatus={teacherEvaluationStatus}
                  teacherModelCancelled={teacherModelCancelled}
                  teacherModelWarning={teacherModelWarning}
                  teacherModelEtaSeconds={teacherModelEtaSeconds}
                  teacherModelQuotaLink={teacherModelQuotaLink}
                  teacherModelAzureMode={teacherModelAzureMode}
                  teacherModelFailureDetails={teacherModelFailureDetails}
                  onCancelTeacherEvaluation={handleCancelTeacherEvaluation}
                  old_model={getComparisonModelName()}
                  compareWith={currentToCompare}
                  currentVersion={`v${selectedVersion}`}
                  currentVersionLabel={selectedVersionHeaderLabels?.versionLabel}
                  currentBenchmarkLabel={selectedVersionHeaderLabels?.benchmarkLabel}
                  currentModelLabel={selectedVersionHeaderLabels?.modelLabel}
                  singleModelView={!currentToCompare}
                  metricsLoading={customThresholdLoading}
                />
                )
              ) : (
                <MetricsChart
                  metrics={comparativeMetrics}
                  showComparison={!!currentToCompare}
                  old_model={getComparisonModelName()}
                  teacherModelProcessing={teacherModelProcessing}
                  teacherModelFailed={teacherModelFailed}
                  studentModelProcessing={studentModelProcessing}
                  compareWith={currentToCompare}
                  currentVersion={`v${selectedVersion}`}
                />
              ))}
              {/* Error Analysis - shown only when a specific label is selected */}
              {selectedLabel !== ALL_LABELS && confusionMatrix && (
                <div style={{ marginTop: 24 }}>
                  <ErrorAnalysis
                    confusionMatrix={confusionMatrix}
                    showComparison={!!currentToCompare}
                    oldModel={getComparisonModelName()}
                    teacherModelProcessing={teacherModelProcessing}
                    studentModelProcessing={studentModelProcessing}
                    compareWith={currentToCompare}
                    currentVersion={`v${selectedVersion}`}
                    accuracy={errorAnalysisAccuracy}
                  />
                </div>
              )}
              {/* Classification: label-vs-label confusion matrix (collapsed by default) */}
              {isClassification && labelConfusionMatrixPayload && (
                <div style={{ marginTop: 24, marginBottom: 16 }}>
                  <LabelConfusionMatrix
                    payload={labelConfusionMatrixPayload}
                    showComparison={!!currentToCompare}
                    oldModelTitle={getComparisonModelName()}
                    newModelTitle={`SpecificAI v${selectedVersion}`}
                    selectedLabel={selectedLabel}
                  />
                </div>
              )}
              {/* NER (not NER_WITH_RELATIONS): pie charts on the Overview tab */}
              {isNer && showEvaluationViewTabs && (
                <div style={{ marginTop: 24 }}>
                  <ModelResponsesTable
                    key={`overview-${localUsecase?._id}`}
                    entityExtractionsMode={EE_RENDER_MODES.OVERVIEW}
                    displayNames={displayNames}
                    llmUsecaseId={localUsecase?._id}
                    version={selectedVersion}
                    selectedDistillationEventId={selectedBenchmarkEventId}
                    currentUsecase={localUsecase}
                    teacherModelProcessing={teacherModelProcessing}
                    teacherModelFailed={teacherModelFailed}
                    teacherModelErrorDetails={teacherModelErrorDetails || teacherModelError}
                    hideTeacherFailedAlert={showEvaluationErrorsAlert}
                    studentModelProcessing={studentModelProcessing}
                    dropdownLabelRender={dropdownLabelRender}
                    compareWith={currentToCompare}
                    comparisonModelName={getComparisonModelName()}
                    hasBenchmark={evaluationBenchmarks.length > 0}
                    evaluationDatasets={evaluationDatasets}
                    evaluationBenchmarks={evaluationBenchmarks}
                    selectedLabel={selectedLabel}
                    selectedLanguage={selectedLanguage}
                    taskType={localUsecase?.task_type}
                    sampleCounters={liveOverviewSampleCounters}
                    activeTab={activeTab}
                    relationTypes={labels}
                  />
                </div>
              )}
            </>
          )}
          {/* SAMPLES: detailed response analysis / entity extractions samples */}
          {(!showEvaluationViewTabs || isSamplesView) && (
            <div style={{ marginTop: showEvaluationViewTabs ? 0 : 24 }}>
              {(isNer || isNerWithRelations) ? (
                <div style={{ marginBottom: "20px" }}>
                  <ModelResponsesTable
                    key={`samples-${localUsecase?._id}`}
                    entityExtractionsMode={
                      showEvaluationViewTabs ? EE_RENDER_MODES.SAMPLES : EE_RENDER_MODES.ALL
                    }
                    displayNames={displayNames}
                    llmUsecaseId={localUsecase?._id}
                    version={selectedVersion}
                    selectedDistillationEventId={selectedBenchmarkEventId}
                    currentUsecase={localUsecase}
                    teacherModelProcessing={teacherModelProcessing}
                    teacherModelFailed={teacherModelFailed}
                    teacherModelErrorDetails={teacherModelErrorDetails || teacherModelError}
                    hideTeacherFailedAlert={showEvaluationErrorsAlert}
                    studentModelProcessing={studentModelProcessing}
                    dropdownLabelRender={dropdownLabelRender}
                    compareWith={currentToCompare}
                    comparisonModelName={getComparisonModelName()}
                    hasBenchmark={evaluationBenchmarks.length > 0}
                    evaluationDatasets={evaluationDatasets}
                    evaluationBenchmarks={evaluationBenchmarks}
                    selectedLabel={selectedLabel}
                    selectedLanguage={selectedLanguage}
                    taskType={localUsecase?.task_type}
                    sampleCounters={liveOverviewSampleCounters}
                    activeTab={activeTab}
                    relationTypes={labels}
                    selectedThresholds={debouncedThresholdsForTable}
                  />
                </div>
              ) : showEvaluationViewTabs ? (
                <div style={{ marginBottom: 20 }}>
                  <ModelResponsesTable
                    key={localUsecase?._id}
                    displayNames={displayNames}
                    llmUsecaseId={localUsecase?._id}
                    version={selectedVersion}
                    selectedDistillationEventId={selectedBenchmarkEventId}
                    currentUsecase={localUsecase}
                    teacherModelProcessing={teacherModelProcessing}
                    teacherModelFailed={teacherModelFailed}
                    teacherModelErrorDetails={teacherModelErrorDetails || teacherModelError}
                    hideTeacherFailedAlert={showEvaluationErrorsAlert}
                    studentModelProcessing={studentModelProcessing}
                    dropdownLabelRender={dropdownLabelRender}
                    compareWith={currentToCompare}
                    comparisonModelName={getComparisonModelName()}
                    hasBenchmark={evaluationBenchmarks.length > 0}
                    evaluationDatasets={evaluationDatasets}
                    evaluationBenchmarks={evaluationBenchmarks}
                    selectedLabel={selectedLabel}
                    selectedLanguage={selectedLanguage}
                    taskType={localUsecase?.task_type}
                    sampleCounters={liveOverviewSampleCounters}
                    activeTab={activeTab}
                    relationTypes={labels}
                    selectedThresholds={debouncedThresholdsForTable}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: 20 }}>
                  <CardDefault
                    type="collapsed"
                    open={false}
                    title="Detailed Response Analysis"
                    subtitle=""
                    showSubtitle={false}
                    border="yes"
                  >
                    <div style={{ marginBottom: 0 }}>
                      <ModelResponsesTable
                        key={localUsecase?._id}
                        displayNames={displayNames}
                        llmUsecaseId={localUsecase?._id}
                        version={selectedVersion}
                        selectedDistillationEventId={selectedBenchmarkEventId}
                        currentUsecase={localUsecase}
                        teacherModelProcessing={teacherModelProcessing}
                        teacherModelFailed={teacherModelFailed}
                        teacherModelErrorDetails={teacherModelErrorDetails || teacherModelError}
                        hideTeacherFailedAlert={showEvaluationErrorsAlert}
                        studentModelProcessing={studentModelProcessing}
                        dropdownLabelRender={dropdownLabelRender}
                        compareWith={currentToCompare}
                        comparisonModelName={getComparisonModelName()}
                        hasBenchmark={evaluationBenchmarks.length > 0}
                        evaluationDatasets={evaluationDatasets}
                        evaluationBenchmarks={evaluationBenchmarks}
                        selectedLabel={selectedLabel}
                        selectedLanguage={selectedLanguage}
                        taskType={localUsecase?.task_type}
                        sampleCounters={liveOverviewSampleCounters}
                        activeTab={activeTab}
                        relationTypes={labels}
                        selectedThresholds={debouncedThresholdsForTable}
                      />
                    </div>
                  </CardDefault>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {loading && !shouldShowProcessingOverlay && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', height: 295,
        }}>
          <Spinner size="large" />
        </div>
      )}
      {!loading && (shouldShowProcessingOverlay || (!evaluationExists || studentModelProcessing)) && (
        <>

          {(versionOptions || []).length > 0 ? (
            // Show progress only if there's active evaluation for the current usecase and version
            localUsecase && selectedVersion && activeEvaluationProgress &&
              !(optimisticRecalc && (activeEvaluationProgress.status === "completed" || activeEvaluationProgress.status === "failed")) ? (
              <>
                <FilterBar
                  compareOptions={compareOptions || []}
                  versionOptions={(versionOptions || []).map(v => ({ label: `v${v}`, value: v }))}
                  versionOptionsRaw={versionOptions || []}
                  selectedVersion={selectedVersion || null}
                  setSelectedVersion={setSelectedVersion}
                  currentToCompare={currentToCompare || null}
                  setCurrentToCompare={setCurrentToCompare}
                  selectedLabel={selectedLabel || ALL_LABELS}
                  setSelectedLabel={setSelectedLabel}
                  languageOptions={languageOptions}
                  selectedLanguage={selectedLanguage}
                  setSelectedLanguage={setSelectedLanguage}
                  getClassOptions={getClassOptions}
                  dropdownLabelRender={dropdownLabelRender}
                  localUsecase={localUsecase}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  moveToScreen={moveToScreen}
                  onImproveClick={() => setImproveOpen(true)}
                  taskType={localUsecase?.task_type}
                  activeTab={activeTab}
                  onRefreshStart={handleRefreshStart}
          evaluationProgress={activeEvaluationProgress}
          onRetrain={onRetrainClick}
                  onRefreshComplete={handleRefreshComplete}
                  distillationEventId={distillationEventId}
                  onGoToModel={distillationEventId ? handleGoToModel : undefined}
                />
                {activeEvaluationProgress.status === "failed"
                  ? renderEvaluationStopped(activeEvaluationProgress.error_details)
                  : (
                    <EvaluationProcessingState
                      progress={activeEvaluationProgress.progress}
                      status={activeEvaluationProgress.status}
                      stepName={activeEvaluationProgress.step_name}
                      totalSteps={activeEvaluationProgress.total_steps}
                      currentStep={activeEvaluationProgress.current_step}
                      initialProgress={initialProgress}
                      initialStepName={initialStepName}
                      initialTotalSteps={initialTotalSteps}
                      initialCurrentStep={initialCurrentStep}
                    />
                  )}
              </>
            ) : (
              <>
                <FilterBar
                  compareOptions={compareOptions || []}
                  versionOptions={(versionOptions || []).map(v => ({ label: `v${v}`, value: v }))}
                  versionOptionsRaw={versionOptions || []}
                  selectedVersion={selectedVersion || null}
                  setSelectedVersion={setSelectedVersion}
                  currentToCompare={currentToCompare || null}
                  setCurrentToCompare={setCurrentToCompare}
                  selectedLabel={selectedLabel || ALL_LABELS}
                  setSelectedLabel={setSelectedLabel}
                  languageOptions={languageOptions}
                  selectedLanguage={selectedLanguage}
                  setSelectedLanguage={setSelectedLanguage}
                  getClassOptions={getClassOptions}
                  dropdownLabelRender={dropdownLabelRender}
                  localUsecase={localUsecase}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  moveToScreen={moveToScreen}
                  onImproveClick={() => setImproveOpen(true)}
                  taskType={localUsecase?.task_type}
                  activeTab={activeTab}
                  onRefreshStart={handleRefreshStart}
          evaluationProgress={activeEvaluationProgress}
          onRetrain={onRetrainClick}
                  onRefreshComplete={handleRefreshComplete}
                  distillationEventId={distillationEventId}
                  onGoToModel={distillationEventId ? handleGoToModel : undefined}
                />
                {optimisticRecalc != null &&
                  selectedVersion != null &&
                  Math.floor(Number(selectedVersion)) === optimisticRecalc.major ? (
                  <EvaluationProcessingState
                    progress={0}
                    status="evaluating"
                    initialProgress={0}
                    initialStepName="Preparing evaluation dataset"
                    initialTotalSteps={6}
                    initialCurrentStep={1}
                  />
                ) : distillationStatus === "failed"
                  ? renderEvaluationStopped(distillationErrorDetails)
                  : (
                    <EvaluationProcessingState
                      status={distillationStatus || "evaluating"}
                      initialProgress={initialProgress}
                      initialStepName={initialStepName}
                      initialTotalSteps={initialTotalSteps}
                      initialCurrentStep={initialCurrentStep}
                    />
                  )}
              </>
            )
          ) : showTrainingInProgressEmptyState ? (
            <EvaluationTrainingInProgressState />
          ) : (
            <EvaluationEmptyState
              outcome={evaluationResult?.last_training_outcome ?? null}
              onRetrain={onRetrainClick}
            />
          )}
        </>
      )}
      </EvaluationViewTabs>
      <ModelDetailsDrawer
        open={modelDetailsOpen}
        onClose={() => setModelDetailsOpen(false)}
        distillationEventId={distillationEventId}
        version={modelDetailsDrawerVersion}
        onDownloadModel={handleModelDrawerDownload}
        onDownloadBenchmark={handleModelDrawerBenchmarkDownload}
        onDeploy={handleModelDrawerDeploy}
        onUndeploy={handleModelDrawerUndeploy}
        title={
          localUsecase?.usecase_name
            ? `${localUsecase.usecase_name}-v${selectedVersion || 1}.0`
            : 'Model details'
        }
        clientId={localUsecase?.client_id || DEFAULT_CLIENT_ID}
        role={role}
        taskType={localUsecase?.task_type}
        selectedBenchmarkEventId={
          evaluationResult?.selected_distillation_event_id || selectedBenchmarkEventId || null
        }
        onSelectBenchmark={handleSelectBenchmark}
        drawerPush={!embeddedInTaskDrawer}
      />
      <ReplaceBenchmarkModal
        open={replaceBenchmarkMajor != null}
        clientId={localUsecase?.client_id || DEFAULT_CLIENT_ID}
        usecaseName={localUsecase?.usecase_name}
        anchorEventId={replaceAnchorEventId}
        activeBenchmarkEventId={replaceActiveEventId}
        currentEventId={displayedDistillationEventId}
        teacherName={replaceTeacherName || null}
        onCancel={() => setReplaceBenchmarkMajor(null)}
        onReplace={handleReplaceBenchmarkConfirm}
      />
      <ConfigureDeploymentFlowModals
        configureFlow={configureFlow}
        allUsecases={allUsecases}
      />
      {versionsDiffOpen && versionsDiffEventIds && versionComparisonLabels ? (
        <VersionsDiffModal
          clientId={localUsecase?.client_id || DEFAULT_CLIENT_ID}
          usecaseId={currentUsecaseId}
          fromEventId={versionsDiffEventIds.fromEventId}
          toEventId={versionsDiffEventIds.toEventId}
          fromVersionLabel={versionComparisonLabels.compareVersionLabel}
          toVersionLabel={versionComparisonLabels.anchorVersionLabel}
          fromBenchmarkLabel={versionComparisonLabels.compareBenchmarkLabel}
          toBenchmarkLabel={versionComparisonLabels.anchorBenchmarkLabel}
          fromModelLabel={versionComparisonLabels.compareModelLabel}
          toModelLabel={versionComparisonLabels.anchorModelLabel}
          taskName={localUsecase?.usecase_name}
          taskType={localUsecase?.task_type}
          singleLabel={
            localUsecase?.task_type === TaskType.classification && !localUsecase?.is_multilabel
          }
          allLabels={labels}
          onClose={() => setVersionsDiffOpen(false)}
        />
      ) : null}
    </div >
  );
};

export const Evaluation = ({ moveToScreen, hideTaskOverview = false, onRetrainClick = null }) => {
  const {
    getCurrentUsecase,
    currentUsecaseId,
    setCurrentUsecaseId,
    currentSubGroup,
    evaluationProgress,
    setEvaluationProgress,
    evaluationResultsReady,
    realtimeResyncTick,
    setProgressStatus,
    trainStatuses,
  } = useContext(GlobalContext);
  const localUsecase = getCurrentUsecase();

  if (!localUsecase) {
    return <LoadingTaskData />;
  }

  // Multi-objective task overview: per-objective summary cards rendered above
  // the existing per-usecase evaluation drawer. Selecting a card sets the
  // active usecase id, so the drawer underneath rerenders for that objective.
  // Single-objective tasks skip this block entirely.
  const isMultiObjectiveTask = (currentSubGroup?.usecases || []).length > 1;
  const taskOverview =
    !hideTaskOverview && currentSubGroup && isMultiObjectiveTask ? (
      <div style={{ marginTop: 20 }}>
        <TaskEvaluationOverview
          subGroup={currentSubGroup}
          activeUsecaseId={currentUsecaseId}
          onSelect={(id) => setCurrentUsecaseId?.(id)}
        />
      </div>
    ) : null;

  if (isGenerativeTask(localUsecase?.task_type)) {
    return (
      <div className={hideTaskOverview ? undefined : 'page-content-shell'}>
        {taskOverview}
        <SummarizationEvaluation
          key={currentUsecaseId}
          currentUsecase={localUsecase}
          moveToScreen={moveToScreen}
          onRetrain={onRetrainClick}
          evaluationProgress={evaluationProgress}
          setEvaluationProgress={setEvaluationProgress}
          evaluationResultsReady={evaluationResultsReady}
          realtimeResyncTick={realtimeResyncTick}
          trainStatuses={trainStatuses}
          embeddedInTaskDrawer={hideTaskOverview}
        />
      </div>
    );
  }

  return (
    <div className={hideTaskOverview ? undefined : 'page-content-shell'}>
      {taskOverview}
      <EvaluationNonSummarization
        key={currentUsecaseId}
        moveToScreen={moveToScreen}
        localUsecase={localUsecase}
        currentUsecaseId={currentUsecaseId}
        evaluationProgress={evaluationProgress}
        setEvaluationProgress={setEvaluationProgress}
        evaluationResultsReady={evaluationResultsReady}
        setProgressStatus={setProgressStatus}
        trainStatuses={trainStatuses}
        embeddedInTaskDrawer={hideTaskOverview}
        onRetrainClick={onRetrainClick}
      />
    </div>
  );
};
